<template>
  <div class="page-container">
    <div class="page-header">
      <div class="d-flex align-items-center w-100">
        <b-button variant="link" class="mr-3" @click="onBack()">
          <v-icon name="buttons/chevrons_left" :size="13"/>
        </b-button>

        <h4 v-if="isBusy" class="mb-0" id="invoice-ellipsis-loading">Загрузка счёта</h4>
        <h4 v-else class="mb-0">{{title}}</h4>

        <span :class="['custom-tag ml-auto mr-2', getStatusStyle(newItem.paid_type)]">
          {{ getStatusName(newItem.paid_type) }}
        </span>

        <b-overlay
          :show="loading"
          rounded
          opacity="0.6"
          spinner-small
          spinner-variant="success"
          class="d-inline-block"
          @hidden="onHiddenExportButtonOverlay"
        >
          <b-button ref="export_file_button" class="custom-btn-secondary mr-1" @click.stop="exportFileGate('docx')">
            <v-icon name="buttons/export" :size="12"/>
            Экспорт в Docx
          </b-button>
        </b-overlay>

        <b-overlay
          :show="loading"
          rounded
          opacity="0.6"
          spinner-small
          spinner-variant="success"
          class="d-inline-block"
          @hidden="onHiddenExportPdfButtonOverlay"
        >
          <b-button ref="export_pdf_file_button" class="custom-btn-secondary mr-1" @click.stop="exportFileGate('pdf')">
            <v-icon name="buttons/export" :size="12"/>
            Экспорт в PDF
          </b-button>
        </b-overlay>

        <b-button class="custom-btn-primary" :disabled="loading" @click="handleSubmit()">
          <b-spinner v-if="loading" small/>
          <v-icon name="buttons/check" :size="13"/>
          Сохранить
        </b-button>
      </div>
    </div>

    <div class="page-body" style="overflow-y: auto;">
      <b-skeleton-wrapper :loading="isBusy">
        <template #loading>
          <b-card no-body class="mb-2">
            <b-card-body class="p-3">
              <b-row>
                <b-col lg="7">
                  <b-card no-body bg-variant="light">
                    <b-card-body>
                      <b-row class="mt-2 mb-3">
                        <b-col lg="4">
                          <b-skeleton width="36%" height="16%" class="mb-2"/>
                          <b-skeleton type="input" width="100%" height="28%" class="mb-2"/>
                          <b-skeleton width="80%" height="35%"/>
                        </b-col>
                        <b-col lg="4">
                          <b-skeleton width="22%" height="16%" class="mb-2"/>
                          <b-skeleton type="input" width="100%" height="28%"/>
                        </b-col>
                        <b-col lg="4">
                          <b-skeleton width="38%" height="16%" class="mb-2"/>
                          <b-skeleton type="input" width="100%" height="28%"/>
                        </b-col>
                      </b-row>
                      <b-row class="pb-3">
                        <b-col lg="6">
                          <b-skeleton width="46%" height="24%" class="mb-2"/>
                          <b-skeleton type="input" width="100%" height="43%"/>
                        </b-col>
                        <b-col lg="6">
                          <b-skeleton width="35%" height="24%" class="mb-2"/>
                          <b-skeleton type="input" width="100%" height="70%"/>
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                  <b-row class="mt-2">
                    <b-col>
                      <b-skeleton type="input" width="100%" height="130%"/>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col lg="5">
                  <b-card no-body bg-variant="light">
                    <b-card-header>
                      <b-skeleton width="13%" height="50%"/>
                    </b-card-header>
                    <b-list-group flush bg-variant="light">
                      <b-list-group-item
                        v-for="i in 8"
                        :key="'skeleton-item-' + i"
                        bg-variant="light"
                        class="d-flex justify-content-between align-items-center"
                        :class="{
                          'mb-2': i === 2 || i === 5 || i === 6,
                          'border-top': i === 3 || i === 6 || i === 7,
                        }"
                      >
                        <!-- Left -->
                        <div class="flex-grow-1">
                          <b-skeleton :width="getLeftSkeletonWidth(i)" height="20%" class="mb-0"/>
                        </div>
                        <!-- Right -->
                        <div>
                          <b-skeleton :width="getRightSkeletonWidth(i)" class="mr-5"/>
                        </div>
                      </b-list-group-item>
                    </b-list-group>
                  </b-card>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
          <b-tabs class="custom-tabs" card>
            <b-tab disabled>
              <template #title>
                <v-icon name="navbar/tasks" :size="13" class="mr-2"/>
                <span>Заявки на смену</span>
              </template>
            </b-tab>
            <b-tab disabled>
              <template #title>
                <v-icon name="buttons/work" :size="13" class="mr-2"/>
                <span>Работы</span>
              </template>
            </b-tab>
            <b-tab disabled>
              <template #title>
                <v-icon name="buttons/payment" :size="13" class="mr-2"/>
                <span>Оплата</span>
              </template>
            </b-tab>
            <b-tab disabled>
              <template #title>
                <v-icon name="navbar/stamp" :size="13" class="mr-2"/>
                <span>Акты</span>
              </template>
            </b-tab>
          </b-tabs>
          <b-card border-variant="white">
            <b-card no-body bg-variant="light">
              <div class="text-center text-muted d-flex align-items-center justify-content-center">
                <div class="spinner">
                  <div class="rect1"></div>
                  <div class="rect2"></div>
                  <div class="rect3"></div>
                  <div class="rect4"></div>
                  <div class="rect5"></div>
                </div>
                <h5>Загрузка заявок</h5>
              </div>
            </b-card>
          </b-card>
        </template>
      </b-skeleton-wrapper>

      <div v-show="!isBusy">
        <b-row>
          <b-col lg="7">
            <b-card class="custom-card p-0">
              <b-card-body>
                <b-row>
                  <b-col lg="4">
                    <b-form-group
                      id="group-numb"
                      label="Счет №"
                      label-for="input-numb"
                      description="Для подстановки номера оставьте поле пустым"
                    >
                      <b-form-input
                        id="input_numb"
                        size="sm"
                        autocomplete="off"
                        v-model.number="newItem.numb">
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col lg="4">
                    <b-form-group
                      id="group-date"
                      label="Дата"
                      label-for="input-date"
                    >
                      <b-form-input
                        id="input-date"
                        size="sm"
                        type="date"
                        v-model.date="newItem.date"/>
                    </b-form-group>
                  </b-col>
                  <b-col lg="4">
                    <b-form-group
                      id="group-contract"
                      label="Договор"
                      label-for="input-contract"
                    >
                      <b-form-input
                        id="input_contract"
                        size="sm"
                        autocomplete="off"
                        v-model="newItem.contract">
                      </b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-group
                  id="group_organization"
                  label="Организация продавец"
                  label-for="input_organization"
                >
                  <template #label>
                    Организация
                    <b-link
                      class="text-decoration-none text-small text-body"
                      v-b-modal.modal-organization
                      v-b-tooltip.hover
                      title="Реквизиты"
                    >
                      <font-awesome-icon icon="id-card" />
                    </b-link>
                    <b-modal id="modal-organization" title="Реквизиты" ok-only size="lg">
                      <p>
                        {{selectedOrganization.full_name}} <br>
                        ИНН/КПП {{ selectedOrganization.inn }} / {{ selectedOrganization.kpp }}<br>
                        ОГРН {{ selectedOrganization.ogrn }}<br>
                        Адрес {{ selectedOrganization.address }}<br>
                        Расчетный счет {{ selectedOrganization.checking_account }}<br>
                        БИК {{ selectedOrganization.bik }} , {{ selectedOrganization.bank }} , Кор. счет {{ selectedOrganization.cor_account }}
                      </p>
                    </b-modal>
                  </template>
                  <b-form-select size="sm" id="input_organization" v-model="selectedOrganization">
                    <option v-for="option in organizations" :value="option">
                      {{ option.name }} (ИНН {{ option.inn }} / КПП {{ option.kpp }})
                    </option>
                  </b-form-select>
                </b-form-group>

                <b-form-group
                  id="group_customer"
                  label="Организация покупатель"
                  label-for="input_customer"
                >
                  <template #label>
                    Заказчик
                    <b-link
                      v-if="customer.name"
                      class="text-decoration-none text-small text-body"
                      v-b-modal.modal-customer
                      v-b-tooltip.hover
                      title="Реквизиты"
                    >
                      <font-awesome-icon icon="id-card" />
                    </b-link>
                    <b-modal id="modal-customer" title="Реквизиты" ok-only size="lg">
                      <p class="text-small">
                        {{customer.full_name}} <br>
                        ИНН/КПП {{ customer.inn }} / {{ customer.kpp }}<br>
                        ОГРН {{ customer.ogrn }}<br>
                        Адрес {{ customer.address }}<br>
                        Расчетный счет {{ customer.checking_account }}<br>
                        БИК {{ customer.bik }} , {{ customer.bank }} , Кор. счет {{ customer.cor_account }}
                      </p>
                    </b-modal>
                  </template>

                  <div v-if="customer.name" >
                    <b-card no-body>
                      <div class="my-1 mx-2 text-small">
                        {{ customer.name ? customer.name + ' (ИНН ' + customer.inn + ' /  КПП ' + customer.kpp + ')' : "-" }}
                      </div>
                    </b-card>
                  </div>
                  <div v-else>Не определён</div>
                </b-form-group>

              </b-card-body>
            </b-card>

            <b-alert
              class="mt-2"
              :show="true"
              :variant="this.newItem.summa !== this.totalPriceTasks ? 'danger' : 'success'"
            >
              {{ this.newItem.summa !== this.totalPriceTasks ?
              'Внимание! Сумма по счёту не совпадает с суммой по заявкам.' :
              'Сумма по счёту совпадает с суммой по заявкам.' }}
            </b-alert>
          </b-col>
          <b-col lg="5">
            <b-card no-body class="custom-card">
              <h6 id="invoice-header" class="text-small text-secondary m-2 p-2">ИТОГО</h6>
              <b-list-group flush bg-variant="light">
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Сумма по счёту
                  <strong>{{priceFormat(this.newItem.summa)}}</strong>
                </b-list-group-item>
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Кол-во работ
                  <strong>{{this.newItem.works.length}}</strong>
                </b-list-group-item>
              </b-list-group>
              <b-list-group flush bg-variant="light" class="mt-2">
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Кол-во заявок
                  <strong>{{this.tasks.length}}</strong>
                </b-list-group-item>
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Единиц(часов, шт.) по заявкам
                  <strong>{{priceFormat(this.totalHoursTasks)}}</strong>
                </b-list-group-item>
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Сумма по заявкам
                  <strong>{{priceFormat(this.totalPriceTasks)}}</strong>
                </b-list-group-item>
              </b-list-group>
              <b-list-group flush bg-variant="light" class="mt-2">
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Кол-во актов
                  <strong>{{this.newItem.acts.length}}</strong>
                </b-list-group-item>
              </b-list-group>
              <b-list-group flush bg-variant="light" class="mt-2">
                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Кол-во оплат
                  <strong>{{this.newItem.payments.length}}</strong>
                </b-list-group-item>

                <b-list-group-item bg-variant="light" class="d-flex justify-content-between align-items-center">
                  Сумма по оплатам
                  <strong>{{priceFormat(this.totalPaymentsSum)}}</strong>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>
        </b-row>

        <b-tabs card class="custom-tabs">
          <b-tab active>
            <template #title>
              <v-icon name="navbar/tasks" :size="13" class="mr-2"/>
              <span>Заявки на смену</span>
            </template>
            <div class="page-body">
              <template v-if="isBusy || (tasks && tasks.length > 0)">
                <div class="page-table-container">
                  <b-table
                    id="invoice-tasks"
                    ref="invoice_tasks"
                    class="custom-table mb-0"
                    selectable
                    select-mode="single"
                    responsive
                    :items="tasks"
                    :fields="fields"
                    no-provider-filtering
                    no-provider-paging
                    no-provider-sorting
                  >
                    <template #table-busy>
                      <div class="text-center text-success my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Загрузка</strong>
                      </div>
                    </template>

                    <template #cell(start_address)="row">
                      <div v-if="row.item.start_address.length<100">{{ row.item.start_address }}</div>
                      <div v-else>{{ row.item.start_address.substring(0, 100) + ".." }}</div>
                      <p class="m-0 p-0 text-secondary text-small">{{row.item.customer_name}}</p>
                    </template>

                    <template #cell(start_date)="row">
                      {{formatTableDateTime(row.item.start_date)}}
                    </template>

                    <template #cell(total_price)="row">
                      {{priceFormat(row.item.total_price)}}
                    </template>

                    <template #cell(paid)="row">
                      {{paid(row)}}
                    </template>

                    <template #cell(read_only)="row">
                      <font-awesome-icon :icon="['fas', readOnly(row).icon]" :style=readOnly(row).style />
                    </template>

                    <template #head(action)="row">
                      <b-btn variant="link" style="color: #1A9167 !important; text-decoration: none !important;" @click.stop="onAddTask()">
                        <v-icon name="buttons/plus" :size="9" class="mr-1"/>
                        Добавить
                      </b-btn>
                    </template>

                    <template #cell(action)="row">
                      <b-btn variant="link" @click="onRemoveTask(row)">
                        <v-icon name="buttons/trash" :size="16"/>
                      </b-btn>
                    </template>
                  </b-table>
                </div>
                <div class="page-pagination">
                  <pagination-footer
                    :total-rows="tasks.length"
                    :per-page="perPage"
                    :current-page.sync="currentPage"
                    @per-page-change="val => onPerPageChange(val, 'perPage', 'currentPage')"
                  >
                    <template #footer-list>
                      Кол-во заявок: <b>{{tasks.length}}</b> |
                      <span v-html="workTypeHours"></span> |
                      Сумма по заявкам: <b>{{priceFormat(totalPriceTasks)}}</b> |
                      Всего работ: <b>{{newItem.works.length}}</b> |
                      Сумма по счёту: <b>{{priceFormat(newItem.summa)}}</b>
                    </template>
                  </pagination-footer>
                </div>
              </template>

              <div v-else class="empty-state-container">
                <v-icon name="navbar/empty_table" :size="90"/>
                <h5>Нет добавленных заявок.</h5>
                <p>Добавьте заявки в счёт прямо сейчас</p>
                <b-btn class="custom-btn-primary mt-1" @click.stop="onAddTask()">
                  <v-icon name="buttons/plus" :size="12" class="mr-1"/>
                  Новая заявка
                </b-btn>
              </div>
            </div>
          </b-tab>

          <b-tab>
            <template #title>
              <v-icon name="buttons/work" :size="13" class="mr-2"/>
              <span>Работы</span>
            </template>
            <div class="page-body">
              <template v-if="isBusy || (newItem.works && newItem.works.length > 0)">
                <div class="page-table-container">
                  <b-table
                    id="invoice-works"
                    ref="invoice_works"
                    class="custom-table mb-0"
                    selectable
                    select-mode="single"
                    responsive
                    :items="newItem.works"
                    :fields="worksFields"
                    :per-page="worksPerPage"
                    :current-page="worksCurrentPage"
                    :sort-by.sync="worksSortBy"
                    :sort-desc.sync="worksSortDesc"
                    :sort-compare="sortCompare"
                    no-provider-filtering
                    no-provider-paging
                    no-provider-sorting
                  >
                    <template #table-busy>
                      <div class="text-center text-success my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Загрузка</strong>
                      </div>
                    </template>

                    <template #cell(index)="row">
                      {{ row.index + 1 }}
                    </template>

                    <template #cell(work_type_id)="row">
                      <b-form-select
                        id="input_work_type"
                        size="sm"
                        v-model="row.item.work_type_id"
                        :options="work_type_catalogs"
                        @change="updateName(row.item)"
                      />
                    </template>

                    <template #cell(name)="row">
                      <b-form-input
                        id="input_name"
                        size="sm"
                        autocomplete="off"
                        v-model="row.item.name"
                      />
                    </template>

                    <template #cell(price)="row">
                      <b-form-input
                        id="input_price"
                        size="sm"
                        class="widthPrice"
                        autocomplete="off"
                        v-model.number="row.item.price"
                      />
                    </template>

                    <template #cell(count)="row">
                      <b-form-input
                        id="input_count"
                        size="sm"
                        class="widthPrice"
                        autocomplete="off"
                        v-model.number="row.item.count"
                      />
                    </template>

                    <template #cell(nds)="row">
                      {{ row.item.nds }}%
                    </template>

                    <template #cell(unit)="row">
                      <unit-select id="input_unit" ref="unit_select" :initial-unit="row.item.unit_code" is-short @selectUnit="updateUnit($event, row)"/>
                    </template>

                    <template #cell(summa)="row">
                      {{priceFormat(row.item.summa)}}
                    </template>

                    <template #head(action)="row">
                      <b-btn variant="link" style="color: #1A9167 !important; text-decoration: none !important;" @click.stop="onAddWork()">
                        <v-icon name="buttons/plus" :size="9" class="mr-1"/>
                        Добавить
                      </b-btn>
                    </template>

                    <template #cell(action)="row">
                      <b-btn variant="link" @click="onRemoveWork(row)">
                        <v-icon name="buttons/trash" :size="16"/>
                      </b-btn>
                    </template>
                  </b-table>
                </div>

                <div class="page-pagination">
                  <pagination-footer
                    :total-rows="newItem.works.length"
                    :per-page="worksPerPage"
                    :current-page.sync="worksCurrentPage"
                    @per-page-change="val => onPerPageChange(val, 'worksPerPage', 'worksCurrentPage')"
                  >
                    <template #footer-list>
                      Кол-во заявок: <b>{{tasks.length}}</b> |
                      Часов по заявкам: <b>{{priceFormat(totalHoursTasks)}}</b> |
                      Сумма по заявкам: <b>{{priceFormat(totalPriceTasks)}}</b> |
                      Всего работ: <b>{{newItem.works.length}}</b> |
                      Сумма по счёту: <b>{{priceFormat(newItem.summa)}}</b>
                    </template>
                  </pagination-footer>
                </div>
              </template>

              <div v-else class="empty-state-container">
                <v-icon name="navbar/empty_table" :size="90"/>
                <h5>Нет добавленных работ.</h5>
                <p>Добавьте работы в счёт прямо сейчас</p>
                <b-btn class="custom-btn-primary mt-1" @click.stop="onAddWork()">
                  <v-icon name="buttons/plus" :size="12" class="mr-1"/>
                  Новая работа
                </b-btn>
              </div>
            </div>
          </b-tab>

          <b-tab>
            <template #title>
              <v-icon name="navbar/stamp" :size="13" class="mr-2"/>
              <span>Акты</span>
            </template>
            <div class="page-body">
              <template v-if="isBusy || (newItem.acts && newItem.acts.length > 0)">
                <div class="page-table-container">
                  <b-table
                    id="invoice-acts"
                    ref="invoice_acts"
                    class="custom-table mb-0"
                    selectable
                    select-mode="single"
                    responsive
                    :items="newItem.acts"
                    :fields="actsFields"
                    no-provider-filtering
                    no-provider-paging
                    no-provider-sorting
                  >
                    <template #table-busy>
                      <div class="text-center text-success my-2">
                        <b-spinner class="align-middle"/>
                        <strong>Загрузка</strong>
                      </div>
                    </template>

                    <template #cell(numb)="row">
                      <b-form-input
                        id="input_number"
                        size="sm"
                        autocomplete="off"
                        v-model.number="row.item.numb"
                      />
                    </template>

                    <template #cell(date)="row">
                      <b-form-input
                        id="input_date"
                        size="sm"
                        type="date"
                        v-model.date="row.item.date"
                      />
                    </template>

                    <template #cell(contract)="row">
                      <b-form-input
                        id="input_contract"
                        size="sm"
                        autocomplete="off"
                        v-model="row.item.contract"
                      />
                    </template>

                    <template #cell(town)="row">
                      <b-form-input
                        id="input_town"
                        size="sm"
                        autocomplete="off"
                        v-model="row.item.town"
                      />
                    </template>

                    <template #cell(document)="row">
                      <b-overlay
                        :show="loading"
                        rounded
                        opacity="0.6"
                        spinner-small
                        spinner-variant="success"
                        class="d-inline-block"
                      >
                        <b-button size="sm" variant="info" :disabled="row.item.id === 0" @click.stop="exportActFileGate(row, 'docx')">
                          <font-awesome-icon icon="file" class="mr-1"/>
                          Docx
                        </b-button>
                      </b-overlay>

                      <b-overlay
                        :show="loading"
                        rounded
                        opacity="0.6"
                        spinner-small
                        spinner-variant="success"
                        class="d-inline-block"
                      >
                        <b-button size="sm" variant="info" :disabled="row.item.id === 0" @click.stop="exportActFileGate(row, 'pdf')">
                          <font-awesome-icon icon="file-pdf" class="mr-1"/>
                          PDF
                        </b-button>
                      </b-overlay>
                    </template>

                    <template #head(action)="row">
                      <b-btn variant="link" style="color: #1A9167 !important; text-decoration: none !important;" @click.stop="onAddAct()">
                        <v-icon name="buttons/plus" :size="9" class="mr-1"/>
                        Добавить
                      </b-btn>
                    </template>

                    <template #cell(action)="row">
                      <b-button size="sm" variant="primary" @click="onEditAct(row.item)">
                        <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                      </b-button>
                      <b-btn variant="link" @click="onRemoveAct(row)">
                        <v-icon name="buttons/trash" :size="16"/>
                      </b-btn>
                    </template>
                  </b-table>
                </div>
                <div class="page-pagination">
                  <pagination-footer
                    :total-rows="newItem.acts.length"
                    :per-page="actsPerPage"
                    :current-page.sync="actsCurrentPage"
                    @per-page-change="val => onPerPageChange(val, 'actsPerPage', 'actsCurrentPage')"
                  >
                    <template #footer-list>
                      Всего актов: <b>{{newItem.acts.length}}</b> |
                      Всего работ: <b>{{newItem.works.length}}</b> |
                      Сумма по счёту: <b>{{priceFormat(newItem.summa)}}</b>
                    </template>
                  </pagination-footer>
                </div>
              </template>

              <div v-else class="empty-state-container">
                <v-icon name="navbar/empty_table" :size="90"/>
                <h5>Нет добавленных актов.</h5>
                <p>Добавьте акты в счёт прямо сейчас</p>
                <b-btn class="custom-btn-primary mt-1" @click.stop="onAddAct()">
                  <v-icon name="buttons/plus" :size="12" class="mr-1"/>
                  Новый акт
                </b-btn>
              </div>
            </div>
          </b-tab>

          <b-tab>
            <template #title>
              <v-icon name="buttons/payment" :size="13" class="mr-2"/>
              <span>Оплата</span>
            </template>
              <div class="page-body">
                <template v-if="isBusy || (newItem.payments && newItem.payments.length > 0)">
                  <div class="page-table-container">
                    <b-table
                      id="invoice-payments"
                      ref="invoice_payments"
                      class="custom-table mb-0"
                      selectable
                      select-mode="single"
                      responsive
                      :items="newItem.payments"
                      :fields="paymentsFields"
                      :per-page="paymentsPerPage"
                      :current-page="paymentsCurrentPage"
                      :sort-by.sync="paymentsSortBy"
                      :sort-desc.sync="paymentsSortDesc"
                      no-provider-filtering
                      no-provider-paging
                      no-provider-sorting
                    >
                      <template #table-busy>
                        <div class="text-center text-success my-2">
                          <b-spinner class="align-middle"/>
                          <strong>Загрузка</strong>
                        </div>
                      </template>

                      <template #cell(numb)="row">
                        <b-form-input
                          id="input_number"
                          size="sm"
                          autocomplete="off"
                          v-model.number="row.item.numb"
                        />
                      </template>

                      <template #cell(date)="row">
                        <b-form-input
                          id="input_date"
                          size="sm"
                          type="date"
                          v-model.date="row.item.date"
                        />
                      </template>

                      <template #cell(sum)="row">
                        <b-form-input
                          id="input_contract"
                          size="sm"
                          class="widthPrice"
                          autocomplete="off"
                          v-model.number="row.item.sum"
                        />
                      </template>

                      <template #cell(comment)="row">
                        <b-form-input
                          id="input_town"
                          size="sm"
                          autocomplete="off"
                          v-model="row.item.comment"
                        />
                      </template>

                      <template #head(action)="row">
                        <b-btn variant="link" style="color: #1A9167 !important; text-decoration: none !important;" @click.stop="onAddPayment()">
                          <v-icon name="buttons/plus" :size="9" class="mr-1"/>
                          Добавить
                        </b-btn>
                      </template>

                      <template #cell(action)="row">
                        <b-btn variant="link" @click="onRemovePayment(row)">
                          <v-icon name="buttons/trash" :size="16"/>
                        </b-btn>
                      </template>
                    </b-table>
                  </div>

                  <div class="page-pagination">
                    <pagination-footer
                      :total-rows="newItem.payments.length"
                      :per-page="paymentsPerPage"
                      :current-page.sync="paymentsCurrentPage"
                      @per-page-change="val => onPerPageChange(val, 'paymentsPerPage', 'paymentsCurrentPage')"
                    >
                      <template #footer-list>
                        Всего оплат: <b>{{newItem.payments.length}}</b> |
                        Сумма по оплатам: <b>{{priceFormat(totalPaymentsSum)}}</b> |
                        Всего работ: <b>{{newItem.works.length}}</b> |
                        Сумма по счёту: <b>{{priceFormat(newItem.summa)}}</b>
                      </template>
                    </pagination-footer>
                  </div>
                </template>

                <div v-else class="empty-state-container">
                  <v-icon name="navbar/empty_table" :size="90"/>
                  <h5>Нет добавленных оплат.</h5>
                  <p>Добавьте оплаты в счёт прямо сейчас</p>
                  <b-btn class="custom-btn-primary mt-1" @click.stop="onAddPayment()">
                    <v-icon name="buttons/plus" :size="12" class="mr-1"/>
                    Новая оплата
                  </b-btn>
                </div>
              </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <invoice-tasks-select ref="invoiceTaskSelect" :tasks=tasks @addToInvoice="addToInvoice"/>

    <export-file ref="exportFile"/>

    <invoice-act ref="invoice_act" @update="updateInvoiceActs"/>
  </div>
</template>

<script>
import invoices from "../../network/invoices";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
import organizations from "../../network/organizations";
import customers from "../../network/customers";
import locations from "../../network/locations";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import invoiceDocumentPatcher from "../../utilites/invoiceDocumentPatcher";
  import unitStore from "../../utilites/unitStore";
  import invoiceActPatcher from "../../utilites/invoiceActPatcher";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import {rubles} from 'rubles';
  import { parse } from "content-disposition-attachment";
    import InvoiceTasksSelect from "./InvoiceTasksSelect";
    import UnitSelect from "../Catalogs/UnitSelect";
    import ExportFile from "./ExportFile";
    import InvoiceAct from "./InvoiceAct"

export default {
  name: "Invoice",
  components: { PaginationFooter, UnitSelect, InvoiceTasksSelect, ExportFile, InvoiceAct },
  mixins: [
    invoices, catalogs, users, organizations, customers, locations,
    helpers, dateFormatter, invoiceDocumentPatcher, unitStore, invoiceActPatcher
  ],
  data() {
    return {
      title:'Новый счёт',
      newItem: {
        id: 0,
        numb: 0,
        date: this.formatDate(new Date()),
        summa: 0,
        customer_id: 0,
        organization_id: 0,
        active: true,
        tasks: [],
        paid_type: 0,
        works: [],
        acts: [],
        payments: [],
      },
      user: {},
      executors: [],
      isBusy: true,
      customer: {
        id: 0,
        name: ''
      },
      paidTypes: [
        { id: 0, name: "Не оплачено" },
        { id: 1, name: "Оплачено" },
        { id: 2, name: "Частично оплачено" },
        { id: 3, name: "Переплата"}
      ],
      customersUrl: 'customers',
      selectedOrganization: {},
      organizations: [],
      perPage: 100,
      currentPage: 1,
      tasks: [],
      fields: [
        {
          key: 'id',
          sortable: true,
        },
        {
          key: 'start_address',
          label: 'Адрес',
          sortable: true,
          class: 'invoice-tasks-address'
        },
        {
          key: 'start_date',
          label: this.$t('message.tasks.table.start_date'),
          sortable: true,
          class:'widthDate'
        },
        {
          key: 'workers_count',
          label: this.$t('message.tasks.table.workers_count'),
          sortable: true,
        },
        {
          key: 'payment_first',
          label: 'Стоимость ед.',
          sortable: true,
        },
        {
          key: 'total_hours',
          label: 'Кол-во ед.',
          sortable: true,
        },
        {
          key: 'total_price',
          label: this.$t('message.tasks.table.total_price'),
          sortable: true,
        },
        {
          key: 'executor_name',
          label: this.$t('message.tasks.table.executor_name'),
          sortable: true,
        },
        {
          key: 'action',
          class: 'invoice-action',
          label: "Действия",
        },
      ],
      work_type_catalogs: [],
      worksPerPage: 10,
      worksCurrentPage: 1,
      worksFields: [
        {
          key: 'index',
          label: '№',
          class: 'widthIndex',
          sortable: false
        },
        {
          key: 'work_type_id',
          label: 'Вид работ',
          class: 'widthCatalog',
          sortable: false
        },
        {
          key: 'name',
          label: 'Наименование',
          sortable: false
        },
        {
          key: 'price',
          label: 'Цена',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'count',
          label: 'Количество',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'unit',
          label: 'Ед. изм.',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'nds',
          label: 'НДС',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'sum',
          label: 'Сумма',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'action',
          label: "Действия",
          class: 'invoice-action',
          sortable: false
        },
      ],
      worksSortBy: 'id',
      worksSortDesc: false,
      nds: 0,
      actsPerPage: 10,
      actsCurrentPage: 1,
      actsFields: [
        {
          key: 'numb',
          label: 'Акт №',
        },
        {
          key: 'date',
          label: 'Дата',
        },
        {
          key: 'contract',
          label: 'Договор (№ и Дата)',
        },
        {
          key: 'town',
          label: 'Место составления (населённый пункт)',
        },
        {
          key: 'sum',
          label: 'Сумма',
          class: 'align-middle'
        },
        {
          key: 'document',
          label: "Документ",
          class: 'widthCatalog',
        },
        {
          key: 'action',
          label: "Действия",
          class: 'invoice-action',
        },
      ],
      paymentsPerPage: 10,
      paymentsCurrentPage: 1,
      paymentsFields: [
        {
          key: 'numb',
          label: '№ Оплаты',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'date',
          label: 'Дата',
          class: 'widthCatalog',
          sortable: true
        },
        {
          key: 'sum',
          label: 'Сумма',
          class: 'widthPrice',
          sortable: false
        },
        {
          key: 'comment',
          label: 'Комментарий',
          sortable: false
        },
        {
          key: 'action',
          label: "Действия",
          class: 'invoice-action',
          sortable: false
        },
      ],
      paymentsSortBy: 'date',
      paymentsSortDesc: true,
      loading: true,
      leftSkeletonWidth: {  1: '38%', 2: '33%', 3: '35%', 4: '72%', 5: '44%', 6: '32%', 7: '32%', 8: '45%' },
      rightSkeletonWidth: {  1: '100%', 2: '50%', 3: '50%', 4: '50%', 5: '100%', 6: '50%', 7: '50%', 8: '50%' },
    }
  },
  async created() {
    const userInfoResult = await this.getUserInfo();
    this.user = userInfoResult.data;
    const usersResult = await this.getUsers();
    this.executors = usersResult.data.items;
    const organizationsResult = await this.getOrganizations();
    this.organizations = organizationsResult.data.items;
    this.work_type_catalogs = await this.getWorkTypeCatalogOptions();

    await this.updateView();
  },
  computed: {
    totalPriceWorks() {
      return this.newItem.works
        .map(obj => obj.sum)
        .reduce((acc, current) => acc + current, 0)
    },
    totalPriceTasks() {
      return this.tasks
        .map(obj => obj.total_price)
        .reduce((acc, current) => acc + current, 0)
    },
    totalHoursTasks() {
      return this.tasks
        .map(obj => obj.total_hours)
        .reduce((acc, current) => acc + current, 0)
    },
    workTypeHours() {
      // Считаем часы по work_type_id
      const hoursByWorkType = this.tasks.reduce((acc, task) => {
        acc[task.work_type_id] = (acc[task.work_type_id] || 0) + task.total_hours;
        return acc;
      }, {});
      // Формируем строку
      const workTypeHoursString = this.work_type_catalogs.map(catalogItem => {
        const workTypeId = catalogItem.value;
        const hours = hoursByWorkType[workTypeId];

        if (hours > 0) {
          return `${catalogItem.text} (часы): <b>${this.priceFormat(hours)}</b>`;
        } else {
          return null;
        }
      }).filter(Boolean);

      return workTypeHoursString.join(' | ');
    },
    totalPaymentsSum() {
      return this.newItem.payments
        .map(obj => obj.sum)
        .reduce((acc, current) => acc + current, 0)
    },

  },
  watch: {
    'newItem.works': {
      handler: function (newValues, _) {
        newValues.forEach(function (tw) {
          tw.sum = tw.count * tw.price;
        }.bind(this));

        this.newItem.summa = this.totalPriceWorks;
      },
      deep: true
    },
    'newItem.payments': {
      handler: function (newValues, _) {
        this.updateStatus();
      },
      deep: true
    }
  },
  methods: {
    stopLoadingView() {
      setTimeout(function() {
        this.isBusy = false;
        this.loading = false;
      }.bind(this), 500);
    },
    async updateView() {
      // Открываем уже созданный счёт
      const invoiceId = this.$route.params.invoice_id;
      if (invoiceId) {
        await this.getInvoice(invoiceId);
        this.stopLoadingView();
        return;
      }

      this.stopLoadingView();

      // Создаем новый счёт с переданными из вне заявками
      const tasks = this.$route.params.tasks;
      if (tasks != null) {
        this.addToInvoice(tasks);
        return;
      }

      // Создаем новый счёт и открываем добавление заявок
      this.onAddTask();
    },
    async getInvoice(invoiceId) {
      const invoiceResult = await this.getInvoiceById(invoiceId);

      if (!invoiceResult || !invoiceResult.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.newItem = invoiceResult.data;

      this.title = `Счёт №${this.newItem.numb}`;

      const date = new Date(invoiceResult.data.date);
      this.newItem.date = this.formatDate(date);

      this.tasks = invoiceResult.data.tasks;
      const firstTask = this.tasks[0];
      if (firstTask) {
        if (firstTask.payment_type_int === 0) {
          this.nds = 0; // физ лицо
        } else if (firstTask.payment_type_int === 1) {
          this.nds = 22; // юр лицо
        } else if (firstTask.payment_type_int === 2) {
          this.nds = 0; // ИП
        } else if (firstTask.payment_type_int === 3) {
          this.nds = 22; // юр лицо смз
        }
      }

      let acts = this.newItem.acts;
      acts.forEach(function (act) {
        act.date = this.formatDate(new Date(act.date));
      }.bind(this));
      this.newItem.acts = acts;

      let payments = this.newItem.payments;
      payments.forEach(function (pay) {
        pay.date = this.formatDate(new Date(pay.date));
      }.bind(this));
      this.newItem.payments = payments;

      //this.updateWorks();

      await this.getCustomer(this.newItem.customer_id);
      await this.getOrganization(this.newItem.organization_id);
    },
    onBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/invoices').catch(()=>{});
      }
    },
    async handleSubmit() {
      this.newItem.customer_id = this.customer.id;

      if (this.tasks.length === 0) {
        this.showSubmitError("Добавьте заявки.");
        return;
      }

      if (this.newItem.customer_id === 0) {
        this.showSubmitError("Не заполнена организация Заказчик. Для заполнения добавьте заявки.");
        return;
      }

      if (this.selectedOrganization.id == null) {
        this.showSubmitError("Выберите организацию продавца.");
        return;
      }

      if (!this.isDateValid(this.newItem.date)) {
        this.showSubmitError("Выберите дату выставления счёта.");
        return;
      }

      const date = this.formatServerDate(new Date(this.newItem.date));

      let summa = this.newItem.summa;
      summa = Math.trunc(summa * 100) / 100;

      const taskIds = this.tasks.map(obj => obj.id);

      let acts = this.newItem.acts;
      acts.forEach(function (act) {
        if (act.numb === '') {
          act.numb = 0;
        }
        act.date = this.formatServerDate(new Date(act.date));
      }.bind(this));

      let payments = this.newItem.payments;
      payments.forEach(function (pay) {
        pay.date = this.formatServerDate(new Date(pay.date));
      }.bind(this));

      if (this.newItem.numb === '') {
        this.newItem.numb = 0;
      }

      const data = {
        id: this.newItem.id,
        date: date,
        summa: summa,
        customer_id: this.newItem.customer_id,
        organization_id: this.selectedOrganization.id,
        active: this.newItem.active,
        task_ids: taskIds,
        paid_type: this.newItem.paid_type,
        works: this.newItem.works,
        numb: this.newItem.numb,
        acts: acts,
        payments: payments,
        contract: this.newItem.contract
      }

      let invoiceResult;
      if (data.id > 0) {
        invoiceResult = await this.putInvoice(data);
      } else {
        invoiceResult = await this.postInvoice(data);
      }

      if (invoiceResult.error) {
        this.showError("Проверьте правильность заполнения полей", invoiceResult.error.message);
        return;
      }

      if (!invoiceResult || !invoiceResult.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.newItem.id = invoiceResult.data.id;
      this.onBack();
    },
    async getCustomer(customer_id) {
      const customerResult = await this.getCustomerById(customer_id);
      this.customer = customerResult.data;
    },
    async getOrganization(organization_id) {
      const organizationResult = await this.getOrganizationById(organization_id);
      this.selectedOrganization = organizationResult.data;
    },
    async getWorkTypeCatalogOptions(data) {
      const result = await this.getWorkTypeCatalogs(data);
      const catalogs = result.data ? result.data.items : null;

      return catalogs
        .sort((x, y) => (Number(y.active) - Number(x.active)))
        .map(e => ({ value: e.id, text: e.name, disabled: !e.active }));
    },
    updateName(item) {
      const selectedOption = this.work_type_catalogs.find((option) => option.value === item.work_type_id);

      if (selectedOption) {
        item.name = `${selectedOption.text} (Объект:)`;
      } else {
        item.name = '';
      }
    },
    onAddTask() {
      if (this.$refs.invoiceTaskSelect != null) {
        this.$refs.invoiceTaskSelect.showModalSelect();
      }
    },
    async addToInvoice(tasks) {
      tasks.forEach(function (task) {
        if (this.existTask(task.id)) {
          return;
        }

        this.tasks.push(task);
      }.bind(this));

      const firstTask = this.tasks[0];
      if (firstTask) {
        await this.getCustomer(firstTask.customer_id);
        const location = (await this.getLocationById(firstTask.location_id)).data;
        if (location.organization_id > 0) {
          await this.getOrganization(location.organization_id)
        }

        if (firstTask.payment_type_int === 0) {
          this.nds = 0; // физ лицо
        } else if (firstTask.payment_type_int === 1) {
          this.nds = 22; // юр лицо
        } else if (firstTask.payment_type_int === 2) {
          this.nds = 0; // ИП
        } else if (firstTask.payment_type_int === 3) {
          this.nds = 22; // юр лицо смз
        }
      }

      this.newItem.summa = this.totalPriceWorks;
      //this.updateWorks();
    },
    existTask(id) {
      return this.tasks.find(x => x.id === id) !== undefined;
    },
    onRemoveTask(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }
      this.setModalDelete(
        row,
        "Удаление заявки на смену",
        `${this.$t("message.tasks.modal.removeDesc")} № ${this.deleteRow.id}?`,
        this.tasks,
      );
    },
    // Для таблицы заявок
    paid(row) {
      return this.yesOrNoText(row.item.paid);
    },
    async exportFileGate(exportType) {
      const stampRes = await this.getStamps(this.newItem.organization_id);
      const ifHasStamp = stampRes.data.stamp || stampRes.data.director_sign;

      if (ifHasStamp !== "") {
        this.$refs.exportFile.show(function (hasStamp) {
          this.exportFile(this.newItem.id, exportType, hasStamp);
        }.bind(this));
      } else {
        await this.exportFile(this.newItem.id, exportType, false);
      }
    },
    onHiddenExportButtonOverlay() {
      this.$refs.export_file_button.focus();
    },
    onHiddenExportPdfButtonOverlay() {
      this.$refs.export_pdf_file_button.focus();
    },
    getFileName(disposition) {
      const defaultName = "untitled";

      if (!disposition) return defaultName;

      try {
        const { attachment, filename } = parse(disposition);
        return attachment ? filename : defaultName;
      } catch (e) {
        console.error(e);
        return defaultName;
      }
    },
    async exportFile(id, exportType, hasStamp) {
      const data = { id, exportType, hasStamp }

      this.loading = true;
      const result = await this.getInvoiceFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание файла. Попробуйте повторить позднее.");
        this.loading = false;
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
      this.loading = false;
    },
    async exportActFileGate(row, exportType) {
      const stampRes = await this.getStamps(this.newItem.organization_id);
      const ifHasStamp = stampRes.data.stamp || stampRes.data.director_sign || stampRes.data.accountant_sign;

      if (ifHasStamp !== "") {
      this.$refs.exportFile.show(function (hasStamp) {
        this.exportActFile(row.item.id, exportType, hasStamp);
      }.bind(this));
      } else {
        await this.exportActFile(row.item.id, exportType, false);
      }
    },
    async exportActFile(id, exportType, hasStamp) {
      const data = {id, exportType, hasStamp}

      this.loading = true;
      const result = await this.getInvoiceActFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание PDF файла. Попробуйте повторить позднее.");
        this.loading = false;
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g, ' ');

      this.handleFileDownload(result.data, fileName);
      this.loading = false;
    },
    onAddWork() {
      const work = {
        id: 0,
        work_type_id: null,
        name: '',
        price: null,
        count: null,
        unit_code: '539',
        nds: this.nds,
        sum: null
      };
      this.newItem.works.unshift(work);
      this.worksCurrentPage = 1;
      this.worksSortDesc = false;
    },
    onRemoveWork(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      this.setModalDelete(
        row,
        "Удаление работы",
        `Вы уверены, что хотите удалить ${this.deleteRow.name.toLowerCase()}?`,
        this.newItem.works,
      );
    },
    // Единицы измерения
    updateUnit(unit, row) {
      row.item.unit_code = unit.code;
    },
    // Автоматическа подстановка Работ в зависимости от заявок. Не реализовано.
    updateWorks() {
      const work_type_ids = new Set(this.tasks.map(obj => obj.work_type_id));
      let works = [];
      work_type_ids.forEach(function (id, index) {
        const tasks = this.tasks.filter(item => item.work_type_id === id);
        const name = this.work_type_catalogs.find(x => x.id === id).name;
        const summa = tasks.reduce((acc, item) => acc + item.total_price_without_transfer, 0);
        const count = tasks.reduce((acc, item) => acc + item.total_hours, 0);
        let price = 0;
        if (count > 0) {
          price = summa / count;
        }

        const work = {
          index: index,
          id: id,
          name: name,
          price: price,
          count: count,
          units: 'чел.ч',
          nds: '22',
          summa: summa
        };
        works.push(work);
      }.bind(this));

      // Добавляем трансфер отдельным видом работ
      const count = this.tasks.reduce((acc, item) => acc + item.total_transfer_count, 0);

      if (count > 0) {
        const transferCatalogId = 2
        const name = this.work_type_catalogs.find(x => x.id === transferCatalogId).name;
        const transfer = this.tasks.reduce((acc, item) => acc + item.total_transfer_price, 0);
        const work = {
          index: work_type_ids.size + 1,
          id: transferCatalogId,
          name: name,
          price: transfer,
          count: count,
          units: 'шт',
          nds: '22',
          summa: transfer
        };
        works.push(work);
      }

      this.newItem.works = works;
    },
    // Акты
    onAddAct() {
      const completion = function(act) {
        this.newItem.acts.push(act);
      }.bind(this);

      const actWorks = this.newItem.works.map(function (item) {
        return {
          sort_index: item.sort_index,
          id: 0,
          work_type_id: item.work_type_id,
          name: item.name,
          price: item.price,
          count: item.count,
          unit_code: item.unit_code,
          nds: item.nds,
          sum: item.sum
        }
      });

      let newAct = {
        id: 0,
        numb: 0,
        date: this.formatDate(new Date()),
        contract: '',
        town: '',
        active: true,
        works: actWorks,
      }
      this.$refs.invoice_act.showModal(newAct, this.nds, completion);
    },
    onEditAct(act) {
      const completion = function(updateAct) {
        act = updateAct;
      }.bind(this);

      this.$refs.invoice_act.showModal(act, this.nds, completion);
    },
    onRemoveAct(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }
      this.setModalDelete(
        row,
        "Удаление акта",
        `Вы уверены, что хотите удалить акт № ${this.deleteRow.numb}?`,
        this.newItem.acts,
      );
    },
    updateInvoiceActs(act) {
      this.newItem.acts.push(act);
    },
    // Оплаты
    onAddPayment() {
      const pay = {
        id: 0,
        numb: 0,
        date: this.formatDate(new Date()),
        sum: this.newItem.summa,
        comment: '',
      };
      this.newItem.payments.unshift(pay);
      this.paymentsCurrentPage = 1;
    },
    onRemovePayment(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }
      this.setModalDelete(
        row,
        "Удаление работы",
        `Вы уверены, что хотите удалить оплату № ${this.deleteRow.numb}?`,
        this.newItem.payments,
      );
    },
    setModalDelete(row, title, message, array) {
      this.showModalDelete(title, message, function () {
        const index = array.findIndex(item => item === row.item);
        array.splice(index, 1);
      }.bind(this));
    },
    updateStatus() {
      if (this.totalPaymentsSum == 0) {
        this.newItem.paid_type = 0;
        return;
      }

      if (this.totalPaymentsSum < this.newItem.summa) {
        this.newItem.paid_type = 2;
        return;
      }

      if (this.totalPaymentsSum === this.newItem.summa) {
        this.newItem.paid_type = 1;
        return;
      }

      if (this.totalPaymentsSum > this.newItem.summa) {
        this.newItem.paid_type = 3;
        return;
      }
    },
    // Статус
    getStatusName(status_id) {
      const status = this.paidTypes.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 0:
          return 'tag-primary';
        case 1:
          return 'tag-success';
        case 2:
          return 'tag-warning';
        case 3:
          return 'tag-warning';
        default:
          return '';
      }
    },
    // Skeleton
    getLeftSkeletonWidth(i) {
      return this.leftSkeletonWidth[i];
    },
    getRightSkeletonWidth(i) {
      return this.rightSkeletonWidth[i];
    },
    onPerPageChange(newPerPage, perPageField = 'perPage', currentPageField = 'currentPage') {
      this[perPageField] = Number(newPerPage);
      this[currentPageField] = 1;
    }
  },
}
</script>

<style>
#invoice-tasks td {
  text-align: center;
  vertical-align: middle;
}

#invoice-tasks th {
  text-align: center;
  vertical-align: middle;
}

#invoice-works td {
  text-align: center;
}

#invoice-works th {
  text-align: center;
  vertical-align: middle;
}

#invoice-acts td {
  text-align: center;
}

#invoice-acts th {
  text-align: center;
  vertical-align: middle;
}

#invoice-payments td {
  text-align: center;
}

#invoice-payments th {
  text-align: center;
  vertical-align: middle;
}

.invoice-action {
  width: 135px;
}

.widthIndex {
  width: 50px;
}

.widthPrice {
  width: 120px;
}

.widthCatalog {
  width: 200px;
}

.invoice-tasks-address {
  min-width: 200px;
  text-align: left !important;
}

#modal-export-file .mb-3 {
  margin-bottom: 0 !important;
}

/* Skeleton table spinner */
.spinner {
  margin: 1%;
  width: 50px;
  height: 40px;
  text-align: center;
  font-size: 10px;
}

.spinner > div {
  background-color: #e0e0e0;
  height: 100%;
  width: 6px;
  display: inline-block;

  -webkit-animation: sk-stretchdelay 1.4s infinite ease-in-out;
  animation: sk-stretchdelay 1.4s infinite ease-in-out;
}

.spinner .rect2 {
  -webkit-animation-delay: -1.1s;
  animation-delay: -1.1s;
}

.spinner .rect3 {
  -webkit-animation-delay: -1.0s;
  animation-delay: -1.0s;
}

.spinner .rect4 {
  -webkit-animation-delay: -0.9s;
  animation-delay: -0.9s;
}

.spinner .rect5 {
  -webkit-animation-delay: -0.8s;
  animation-delay: -0.8s;
}

@-webkit-keyframes sk-stretchdelay {
  0%, 40%, 100% { -webkit-transform: scaleY(0.4) }
  20% { -webkit-transform: scaleY(1.0) }
}

@keyframes sk-stretchdelay {
  0%, 40%, 100% {
    transform: scaleY(0.4);
    -webkit-transform: scaleY(0.4);
  }  20% {
       transform: scaleY(1.0);
       -webkit-transform: scaleY(1.0);
     }
}

#invoice-header {
  margin-bottom: 0 !important;
  margin-top: 0.5rem !important;
}

#invoice-ellipsis-loading {
  width: 170px;
  white-space: nowrap;
  display: inline-block;
}

@keyframes blink {
  0% { content: "."; }
  33% { content: ".."; }
  66% { content: "..."; }
  100% { content: "."; }
}

#invoice-ellipsis-loading::after {
  content: ".";
  animation: blink 1.5s infinite step-start;
}
</style>



// WEBPACK FOOTER //
// src/components/Invoices/Invoice.vue